import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaEnvelope,
  FaIndustry,
  FaLayerGroup,
  FaWpforms,
  FaThLarge,
  FaUserShield,
  FaUsersCog,
  FaSignOutAlt,
  FaBars,
  FaTimes
} from "react-icons/fa";
import Swal from "sweetalert2";
import "./Sidebar.css";
import A2ZLogo from "../../Shared/Images/A2Zlogo.jpeg";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const MENU_SECTIONS = [
  {
    id: "main",
    label: "MAIN",
    items: [
      {
        label: "Tenants",
        icon: FaBuilding,
        to: "/superadmin/tenants/tenant-table",
        paths: ["/superadmin/tenants/tenant-table" ,"/superadmin/tenants/view-tenant/"]
      },
      {
        label: "Invitation",
        icon: FaEnvelope,
        to: "/superadmin/invitation",
        paths: ["/superadmin/invitation"]
      }
    ]
  },
  {
    id: "catalog",
    label: "CATELOG",
    items: [
      {
        label: "Industries",
        icon: FaIndustry,
        to: "/catalog/industries",
        paths: ["/catalog/industries", "/catalog/add-industry"]
      },
      {
        label: "Scope Catalogs",
        icon: FaLayerGroup,
        to: "/catalog/scope-catalogs",
        paths: ["/catalog/scope-catalogs", "/catalog/add-scope-catalog"]
      },
      {
        label: "Form Modules",
        icon: FaWpforms,
        to: "/catalog/form-modules",
        paths: ["/catalog/form-modules", "/catalog/add-form-module"]
      },
      {
        label: "Scope Modules",
        icon: FaThLarge,
        to: "/catalog/scope-modules",
        paths: ["/catalog/scope-modules", "/catalog/add-scope-module"]
      },
      {
        label: "Form Fields",
        icon: FaWpforms,
        to: "/catalog/form-fields",
        paths: ["/catalog/form-fields"]
      }
    ]
  },
  {
    id: "qualion",
    label: "QUALION USERS",
    items: [
      {
        label: "Roles",
        icon: FaUserShield,
        to: "/qualion/roles",
        paths: ["/qualion/roles"]
      },
      {
        label: "Administrators",
        icon: FaUsersCog,
        to: "/qualion/users",
        paths: ["/qualion/users"]
      }
    ]
  }
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem("ta_sidebar_collapsed") === "true"
  );
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const isActive = (paths) => {
    return paths.some(
      (path) =>
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`)
    );
  };

  useEffect(() => {
    document.body.classList.toggle("ta-sidebar-collapsed", isCollapsed);
    localStorage.setItem("ta_sidebar_collapsed", String(isCollapsed));

    return () => {
      document.body.classList.remove("ta-sidebar-collapsed");
    };
  }, [isCollapsed]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const storedUser = localStorage.getItem("super_admin_user");

    if (!storedUser) return;

    try {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      const userId = parsedUser?.id || parsedUser?.user_id;
      const token =
        localStorage.getItem("super_admin_token") ||
        localStorage.getItem("token");

      if (userId) {
        fetchUserDetails(userId, token);
      }
    } catch (error) {
      console.error("Error parsing super admin user:", error);
    }
  }, []);

  const fetchUserDetails = async (userId, token) => {
    try {
      const response = await fetch(`${API_BASE}/users/${userId}`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`
            }
          : {}
      });

      if (!response.ok) return;

      const data = await response.json();

      setUserDetails(data?.data || data?.user || data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleExternalToggle = () => {
      toggleSidebar();
    };

    window.addEventListener("ta-toggle-sidebar", handleExternalToggle);

    return () => {
      window.removeEventListener("ta-toggle-sidebar", handleExternalToggle);
    };
  }, []);

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb"
    });

    if (!result.isConfirmed) return;

    localStorage.removeItem("super_admin_user");
    localStorage.removeItem("super_admin_token");
    localStorage.removeItem("token");
    localStorage.removeItem("admin_token");

    navigate("/login");
  };

  const displayName =
    userDetails?.name ||
    userDetails?.full_name ||
    userDetails?.username ||
    user?.name ||
    user?.full_name ||
    user?.username ||
    "Super Admin";

  const displayEmail =
    userDetails?.email ||
    user?.email ||
    "superadmin@example.com";

  const getInitials = (name) => {
    if (!name) return "SA";

    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  return (
    <>
      <div
        className={`ta-mobile-overlay ${isMobileOpen ? "show" : ""}`}
        onClick={() => setIsMobileOpen(false)}
      />

      <aside
        className={`ta-sidebar ${isCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
      >
        <div className="ta-sidebar-header">
          <div className="ta-logo-icon">
            <img src={A2ZLogo} alt="A2Z Logo" />
          </div>

          <div className="ta-sidebar-header-text">
            <h6>Super Admin</h6>
            <small>Administration Panel</small>
          </div>

          <button
            type="button"
            className="ta-sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <FaBars />
          </button>
        </div>

        <nav className="ta-sidebar-scroll">
          {MENU_SECTIONS.map((section) => (
            <div className="ta-menu-section-group" key={section.id}>
              <div className="ta-menu-section-heading">
                <span className="ta-section-heading-line"></span>
                <span className="ta-section-heading-text">
                  {section.label}
                </span>
              </div>

              <div className="ta-menu-section-items">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.paths);

                  return (
                    <div className="ta-menu-section" key={item.label}>
                      <Link
                        to={item.to}
                        className={`ta-menu-title ${
                          active ? "active" : ""
                        }`}
                        title={isCollapsed ? item.label : ""}
                      >
                        <span className="ta-menu-icon">
                          <Icon />
                        </span>

                        <span className="ta-menu-label">
                          {item.label}
                        </span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="ta-sidebar-footer">
          <div className="ta-user-info-wrapper">
            <div className="ta-avatar">
              {userDetails?.profile_image || user?.profile_image ? (
                <img
                  src={userDetails?.profile_image || user?.profile_image}
                  alt={displayName}
                />
              ) : (
                getInitials(displayName)
              )}
            </div>

            <div className="ta-user-details">
              <p className="ta-sidebar-user-name">{displayName}</p>
              <span className="ta-user-email">{displayEmail}</span>
            </div>

            <button
              type="button"
              className="ta-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt />
            </button>
          </div>
        </div>
      </aside>

      <button
        type="button"
        className="ta-mobile-toggle"
        onClick={toggleMobileSidebar}
        title={isMobileOpen ? "Close Menu" : "Open Menu"}
      >
        {isMobileOpen ? <FaTimes /> : <FaBars />}
      </button>
    </>
  );
};

export default Sidebar;